import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../services/api";
import grids from "../responsive-fixes/grids.module.css";

type Tab = "compose" | "templates" | "log";

interface LogItem {
  id: number;
  to: string;
  subject: string;
  status: string;
  sent_at: string;
}

const C = {
  bg: "#060e20",
  glass: "rgba(255,255,255,0.02)",
  border: "rgba(189,178,255,0.15)",
  accent: "#bdb2ff",
  green: "#cfeda2",
  danger: "#ff6e84",
  text: "#dde5ff",
  muted: "#6d758b",
};

const glass: React.CSSProperties = {
  background: C.glass,
  backdropFilter: "blur(60px)",
  WebkitBackdropFilter: "blur(60px)",
  border: `0.5px solid ${C.border}`,
  borderRadius: 16,
  padding: 24,
};

const EmailIntelligence: React.FC = () => {
  const [tab, setTab] = useState<Tab>("compose");
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [log, setLog] = useState<LogItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const loadLog = useCallback(async () => {
    try {
      const data = await api.email.log();
      setLog(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      console.error("Failed to load email log:", err);
      setError("Failed to load email log");
      setLog([]);
    }
  }, []);

  useEffect(() => {
    if (tab === "log") loadLog();
  }, [tab, loadLog]);

  const handleGenerate = async () => {
    if (!prompt) return;
    setGenerating(true);
    setError(null);
    try {
      const data = await api.email.generate({ prompt, tone: "follow-up" });
      if (data.body) {
        setBody(data.body);
        if (!subject && data.subject) {
          setSubject(data.subject);
        }
      } else if (typeof data === "string") {
        setBody(data);
      }
    } catch (err) {
      console.error("Generate failed:", err);
      setError("Failed to generate email");
    } finally {
      setGenerating(false);
    }
  };

  const handleSend = async () => {
    if (!to || !subject || !body) {
      setError("Please fill in all fields");
      return;
    }
    setSending(true);
    setError(null);
    try {
      const result = await api.email.send({ to, subject, body, html: true });
      if (result.success) {
        setTo("");
        setSubject("");
        setBody("");
        setPrompt("");
        setError(null);
        loadLog();
      } else {
        setError("Failed to send email");
      }
    } catch (err) {
      console.error("Send failed:", err);
      setError("Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const tabs: Tab[] = ["compose", "templates", "log"];

  return (
    <div style={{ minHeight: "100vh", background: C.bg, padding: "2rem", fontFamily: "Manrope, sans-serif" }}>
      <h1 style={{ color: C.text, fontSize: 24, fontWeight: 700, marginBottom: "1.5rem" }}>
        Email Intelligence
      </h1>

      {error && (
        <div style={{
          background: `${C.danger}22`,
          border: `1px solid ${C.danger}`,
          borderRadius: 8,
          padding: "0.75rem 1rem",
          marginBottom: "1rem",
          color: C.danger,
          fontSize: 13,
        }}>
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              float: "right",
              background: "none",
              border: "none",
              color: C.danger,
              cursor: "pointer",
              fontWeight: 700,
            }}
          >
            ×
          </button>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: "1.5rem" }}>
        {tabs.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: "0.5rem 1.25rem",
            borderRadius: 8,
            border: `1px solid ${tab === t ? C.accent : C.border}`,
            background: tab === t ? `${C.accent}22` : "transparent",
            color: tab === t ? C.accent : C.muted,
            cursor: "pointer",
            fontWeight: 600,
            textTransform: "capitalize",
          }}>{t}</button>
        ))}
      </div>

      {tab === "compose" && (
        <div style={{ ...glass, maxWidth: 700 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ color: C.muted, fontSize: 12 }}>AI Prompt</label>
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <input value={prompt} onChange={e => setPrompt(e.target.value)}
                placeholder="e.g. Follow up with salon about ENZO dryer wholesale"
                style={{ flex: 1, padding: "0.5rem 0.75rem", background: "rgba(255,255,255,0.05)", border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13 }} />
              <button onClick={handleGenerate} disabled={generating || !prompt} style={{
                padding: "0.5rem 1rem", borderRadius: 8, border: `1px solid ${C.accent}`,
                background: `${C.accent}22`, color: C.accent, cursor: generating || !prompt ? "not-allowed" : "pointer", fontWeight: 700, fontSize: 13, opacity: generating || !prompt ? 0.5 : 1,
              }}>{generating ? "..." : "Generate"}</button>
            </div>
          </div>
          {["to", "subject"].map(field => (
            <div key={field} style={{ marginBottom: 12 }}>
              <label style={{ color: C.muted, fontSize: 12, textTransform: "capitalize" }}>{field}</label>
              <input value={field === "to" ? to : subject}
                onChange={e => field === "to" ? setTo(e.target.value) : setSubject(e.target.value)}
                style={{ display: "block", width: "100%", marginTop: 4, padding: "0.5rem 0.75rem", background: "rgba(255,255,255,0.05)", border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, boxSizing: "border-box" }} />
            </div>
          ))}
          <div style={{ marginBottom: 16 }}>
            <label style={{ color: C.muted, fontSize: 12 }}>Body</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={8}
              style={{ display: "block", width: "100%", marginTop: 4, padding: "0.75rem", background: "rgba(255,255,255,0.05)", border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, resize: "vertical", boxSizing: "border-box" }} />
          </div>
          <button onClick={handleSend} disabled={sending || !to || !subject || !body} style={{
            padding: "0.6rem 1.5rem", borderRadius: 8, border: "none",
            background: `linear-gradient(135deg, ${C.green}44, ${C.accent}44)`,
            color: C.text, cursor: sending || !to || !subject || !body ? "not-allowed" : "pointer", fontWeight: 700, fontSize: 14, opacity: sending || !to || !subject || !body ? 0.5 : 1,
          }}>{sending ? "Sending..." : "Send Email"}</button>
        </div>
      )}

      {tab === "templates" && (
        // M2 fix (audit): 280px minmax overflows narrow phones.
        <div className={grids["responsive-grid"]} style={{ gap: 16 }}>
          {["Wholesale Follow-up", "Quote Request", "Invoice", "Welcome", "Order Confirmation"].map(t => (
            <div key={t} style={{ ...glass, cursor: "pointer" }} onClick={() => { setTab("compose"); setSubject(t); }}>
              <div style={{ color: C.accent, fontWeight: 700, marginBottom: 8 }}>{t}</div>
              <div style={{ color: C.muted, fontSize: 12 }}>Click to use this template</div>
            </div>
          ))}
        </div>
      )}

      {tab === "log" && (
        <div style={{ ...glass }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ color: C.text, margin: 0 }}>Email Log</h3>
            <button onClick={loadLog} style={{
              padding: "0.4rem 1rem", borderRadius: 6, border: `1px solid ${C.border}`,
              background: "transparent", color: C.muted, cursor: "pointer", fontSize: 12,
            }}>Refresh</button>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {["To", "Subject", "Status", "Sent At"].map(h => (
                  <th key={h} style={{ padding: "0.75rem 1rem", textAlign: "left", color: C.muted, fontWeight: 700 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {log.length === 0
                ? <tr><td colSpan={4} style={{ padding: "2rem", textAlign: "center", color: C.muted }}>No emails sent yet.</td></tr>
                : log.map(item => (
                  <tr key={item.id} style={{ borderBottom: `0.5px solid ${C.border}` }}>
                    <td style={{ padding: "0.75rem 1rem", color: C.text }}>{item.to}</td>
                    <td style={{ padding: "0.75rem 1rem", color: C.muted }}>{item.subject}</td>
                    <td style={{ padding: "0.75rem 1rem" }}>
                      <span style={{ color: item.status === "sent" ? C.green : C.danger }}>{item.status}</span>
                    </td>
                    <td style={{ padding: "0.75rem 1rem", color: C.muted }}>{item.sent_at?.slice(0, 16)}</td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EmailIntelligence;